import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { setClerkAuthInfo, clearClerkAuthInfo } from '../../store/defaultBaseQuery';
import { useGetUserAttributesQuery } from '../../store/userApi';
import { useDispatch } from 'react-redux';
import { setUser, clearUser } from '../../store/userSlice';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Component that handles integration between Clerk and the backend
 * 
 * This component:
 * 1. Gets the JWT token from Clerk when user is authenticated
 * 2. Sets up the token for all API requests
 * 3. Syncs user information with Redux state
 * 4. Redirects to dashboard when user is already logged in (except in specific flows)
 */
const ClerkAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthSetupComplete, setIsAuthSetupComplete] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [shouldSkipFetch, setShouldSkipFetch] = useState(false);
  
  // List of paths where we don't want automatic dashboard redirection
  const excludedRedirectPaths = [
    '/signup/verify',
    '/signup/invitation'
  ];
  
  // Create a fallback user object from Clerk data
  const createFallbackUser = useCallback(() => {
    if (isSignedIn && user) {
      console.log('[Auth] Creating fallback user object from Clerk data');
      
      // Check if the user email might contain admin/enterprise permissions
      const email = user.primaryEmailAddress?.emailAddress || '';
      const domainParts = email.split('@');
      const domain = domainParts.length > 1 ? domainParts[1] : '';
      
      // Create fallback user without assuming admin role from Clerk metadata
      // Admin role will be determined by the local database for each instance
      const fallbackUser = {
        id: user.id,
        email: email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        plan: 'ENTERPRISE', // Default to ENTERPRISE plan in enterprise version
        hasActiveSubscription: true,
        hasProFeaturesAccess: true,
        isVerified: true,
        role: 'USER', // Default to USER, will be updated from local database
        prompts: [],
        clerkId: user.id
      };
      
      // Set the fallback user in Redux
      dispatch(setUser(fallbackUser as any));
      
      return fallbackUser;
    }
    return null;
  }, [isSignedIn, user, dispatch]);
  
  // Query to get user attributes from backend
  const { 
    data: userAttributes, 
    refetch, 
    error: userAttributesError, 
    isLoading,
    isSuccess
  } = useGetUserAttributesQuery(undefined, {
    // Only call API when user is authenticated and we're not skipping fetches
    skip: !isSignedIn || !isAuthSetupComplete || shouldSkipFetch,
    // Refresh data every 10 minutes to keep session active
    pollingInterval: 10 * 60 * 1000,
    // Don't use cache for this query
    refetchOnMountOrArgChange: true
  });

  // Effect to redirect to dashboard when there's an active session
  useEffect(() => {
    // If user is signed in, is on a login/signup page but not in a special flow
    if (isSignedIn && isAuthSetupComplete && (userAttributes || shouldSkipFetch)) {
      const currentPath = location.pathname;
      const isLoginOrSignup = currentPath === '/login' || 
                              currentPath === '/signup' || 
                              currentPath.startsWith('/login/') || 
                              currentPath.startsWith('/signup/');
      
      // Check if we're in an excluded path
      const isExcludedPath = excludedRedirectPaths.some(path => 
        currentPath.startsWith(path)
      );
      
      // If we're on login/signup but not on an excluded path, redirect to dashboard
      if (isLoginOrSignup && !isExcludedPath) {
        console.log('[Auth] Active session detected on login/signup page. Redirecting to dashboard.');
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isSignedIn, isAuthSetupComplete, userAttributes, shouldSkipFetch, location.pathname, navigate]);

  // Effect to clean up information when user signs out
  useEffect(() => {
    if (!isSignedIn && isAuthSetupComplete) {
      clearClerkAuthInfo();
      dispatch(clearUser());
      console.log('[Auth] User signed out, cleared auth info');
      setRetryCount(0);
      setShouldSkipFetch(false);
    }
  }, [isSignedIn, isAuthSetupComplete, dispatch]);

  // Effect to set up tokens when user signs in or changes
  useEffect(() => {
    const setupAuth = async () => {
      if (isSignedIn && user) {
        try {
          setAuthError(null);
          
          // Get JWT token from Clerk
          const token = await getToken();
          
          if (!token) {
            console.error('[Auth] No token available from Clerk');
            setAuthError('No authentication token available');
            return;
          }
          
          // Save auth data for API requests
          setClerkAuthInfo(
            token, 
            user.id,
            user.primaryEmailAddress?.emailAddress
          );
          
          // Store clerk user ID in localStorage for admin service calls
          localStorage.setItem('clerk-user-id', user.id);
          
          console.log('[Auth] Clerk token set successfully:', {
            userId: user.id,
            email: user.primaryEmailAddress?.emailAddress
          });
          
          setIsAuthSetupComplete(true);
          
          // Don't immediately refetch - wait for the next render cycle when 
          // isAuthSetupComplete has been updated and the query will be enabled
        } catch (error) {
          console.error('[Auth] Error setting Clerk token:', error);
          setAuthError('Error during authentication setup');
        }
      } else if (!isSignedIn) {
        // If user is not authenticated, mark as completed to not block UI
        setIsAuthSetupComplete(true);
      }
    };
    
    setupAuth();
  }, [isSignedIn, user, getToken]);
  
  // Add a separate effect that handles refetching after auth is set up
  useEffect(() => {
    // Only refetch if auth setup is complete, user is signed in, and we're not skipping fetches
    if (isAuthSetupComplete && isSignedIn && !shouldSkipFetch) {
      console.log('[Auth] Auth setup complete, initializing user data query');
      // Wait a moment to ensure query is properly initialized
      const timer = setTimeout(() => {
        try {
          refetch();
        } catch (error) {
          console.error('[Auth] Error during refetch:', error);
          // If refetch fails, use fallback user
          createFallbackUser();
          setShouldSkipFetch(true);
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthSetupComplete, isSignedIn, shouldSkipFetch, refetch, createFallbackUser]);
  
  // Effect to sync user data from backend with Redux
  useEffect(() => {
    if (isSuccess && userAttributes) {
      // Reset retry counter on success
      setRetryCount(0);
      
      // For enterprise version: Always grant access regardless of subscription status
      const enterpriseUserData = {
        ...userAttributes,
        // Set enterprise defaults
        hasActiveSubscription: true,
        hasProFeaturesAccess: true,
        plan: 'ENTERPRISE'
      };
      
      dispatch(setUser(enterpriseUserData));
      console.log('[Auth] User data synced with backend (Enterprise mode):', {
        id: userAttributes.id,
        email: userAttributes.email,
        plan: 'ENTERPRISE',
        hasActiveSubscription: true
      });
    } else if (userAttributesError) {
      console.error('[Auth] Error fetching user data:', userAttributesError);
      
      // Implement exponential backoff for retries
      const handleRetry = () => {
        // Only retry if we're not already at max retries and not already skipping fetches
        if (retryCount < 3 && !shouldSkipFetch) {
          const nextRetryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
          console.log(`[Auth] Will retry in ${nextRetryDelay}ms (attempt ${retryCount + 1})`);
          
          setRetryCount(prev => prev + 1);
          
          // Use a timeout to delay the retry
          const retryTimer = setTimeout(() => {
            // Only try to refetch if we haven't decided to skip fetches in the meantime
            if (!shouldSkipFetch) {
              try {
                refetch();
              } catch (error) {
                console.error('[Auth] Error during retry refetch:', error);
                // If we fail to refetch, use fallback
                createFallbackUser();
                setShouldSkipFetch(true);
              }
            }
          }, nextRetryDelay);
          
          return retryTimer;
        } else if (retryCount >= 3 && !shouldSkipFetch) {
          console.log('[Auth] Maximum retries reached, using fallback data');
          // Create fallback user and skip further fetches until page reload
          createFallbackUser();
          setShouldSkipFetch(true);
          return null;
        }
        return null;
      };
      
      // If there's a 500 server error, don't retry more than once to avoid spamming the server
      if (userAttributesError.toString().includes('500')) {
        if (retryCount === 0) {
          console.log('[Auth] Server error (500), trying once more...');
          const timerRef = handleRetry();
          return () => {
            if (timerRef) clearTimeout(timerRef);
          };
        } else {
          // If we've already tried once with a 500 error, go straight to fallback
          if (!shouldSkipFetch) {
            console.log('[Auth] Persistent server error, using fallback data');
            createFallbackUser();
            setShouldSkipFetch(true);
          }
        }
      } 
      // For other errors, follow the normal retry logic
      else if (retryCount < 3) {
        const timerRef = handleRetry();
        return () => {
          if (timerRef) clearTimeout(timerRef);
        };
      }
      
      // If there's a 401 error (Unauthorized), session may have expired
      if (userAttributesError.toString().includes('401')) {
        console.log('[Auth] Session expired or invalid, refreshing token');
        // Try to refresh token
        const refreshAuth = async () => {
          if (isSignedIn && user) {
            try {
              const newToken = await getToken({ skipCache: true });
              if (newToken) {
                setClerkAuthInfo(
                  newToken, 
                  user.id,
                  user.primaryEmailAddress?.emailAddress
                );
                console.log('[Auth] Token refreshed');
                // Try to get user data again
                try {
                  refetch();
                } catch (error) {
                  console.error('[Auth] Error after token refresh:', error);
                  createFallbackUser();
                  setShouldSkipFetch(true);
                }
              }
            } catch (e) {
              console.error('[Auth] Failed to refresh token:', e);
              // Fallback to Clerk data on token refresh failure
              createFallbackUser();
              setShouldSkipFetch(true);
            }
          }
        };
        refreshAuth();
      }
    }
  }, [userAttributes, userAttributesError, isSuccess, dispatch, isSignedIn, user, getToken, refetch, retryCount, shouldSkipFetch, createFallbackUser]);

  // If there's an auth error, show it
  if (authError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography color="error" variant="body1">
          Authentication error: {authError}. Please try refreshing the page.
        </Typography>
      </Box>
    );
  }
  
  // If we're loading user data and user is authenticated, show spinner
  // But only show the spinner if we're not using the fallback data
  if (isSignedIn && isAuthSetupComplete && isLoading && !userAttributes && !shouldSkipFetch) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return <>{children}</>;
};

export default ClerkAuthProvider; 